import posthog from "posthog-js";
import { isPrerender } from "./isPrerender";

/**
 * PostHog initialization — loaded alongside Segment to enable advanced features
 * (session replay, autocapture, heatmaps, surveys, feature flags, toolbar).
 *
 * Segment handles: page views, identify, group, track
 * PostHog JS handles: autocapture, session replay, pageleaves, feature flags, etc.
 *
 * See: https://posthog.com/docs/libraries/segment
 */

const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY as string | undefined;
const POSTHOG_HOST = "https://us.i.posthog.com";

export function initPostHog(): void {
  if (isPrerender()) {
    // Skip during prerender (Puppeteer/headless) — session replay and
    // capture_exceptions keep the network busy and break `networkidle0`.
    return;
  }

  if (!POSTHOG_KEY) {
    if (import.meta.env.DEV) {
      console.warn("[PostHog] VITE_POSTHOG_KEY is not configured. PostHog disabled.");
    }
    return;
  }

  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    // Link PostHog to Segment so events flow through the Segment pipeline
    segment: (window as any).analytics,
    // Segment handles pageview tracking via analytics.page()
    capture_pageview: false,
    // Enable pageleave for bounce rate, time-on-page, scrollmaps
    capture_pageleave: true,
    // Automatically capture uncaught exceptions and unhandled promise rejections
    // with full stack traces. See: https://posthog.com/docs/error-tracking
    capture_exceptions: true,
    // When PostHog library has loaded, fire the initial page view through Segment
    loaded: () => {
      if ((window as any).analytics) {
        (window as any).analytics.page();
      }
    },
  });

  if (import.meta.env.DEV) {
    console.log("[PostHog] Initialized with error tracking, session replay, autocapture, pageleaves");
  }
}

/**
 * Manually capture an exception with PostHog. Use this in try/catch blocks,
 * React error boundaries, or async handlers where exceptions wouldn't bubble
 * to the global handler.
 *
 * Safe to call even if PostHog is not initialized (no-op).
 */
export function captureException(
  error: unknown,
  additionalProperties?: Record<string, unknown>
): void {
  try {
    const err = error instanceof Error ? error : new Error(String(error));
    posthog.captureException(err, additionalProperties);
  } catch (e) {
    if (import.meta.env.DEV) {
      console.warn("[PostHog] Failed to capture exception:", e);
    }
  }
}

export { posthog };
export default posthog;
